import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppStore } from "@hooks/useAppStore";
import { trackCustomEvent } from "analytics/index";
import { useEffect, useMemo } from "react";
import { useRedeemCopilot } from "../../context";
import { prepareProperties } from "../helpers/properties";
import type { EventContextProperties, RedeemCopilotEvent } from "../types";

type Properties = EventContextProperties & Record<string, unknown>;

export const useRedeemCopilotAnalytics = (
	eventName?: RedeemCopilotEvent,
	properties?: Properties,
	runOnMount = false,
) => {
	const { currentExamContext } = useExamContext();
	const tenantId = useAppStore((store) => store.tenant.id);
	const tenantReadableName = useAppStore((store) => store.tenant.name);

	const { userLogicalId, username, email } = useAuthContext();

	const { analyticsEnabled } = useRedeemCopilot();

	const baseProperties = useMemo(
		() => ({
			userId: userLogicalId,
			username,
			userEmail: email,
			examId: `${currentExamContext.examId}`,
			examName: currentExamContext.examName,
			examDate: currentExamContext.examDate,
			studyPackProductNumericId: currentExamContext.studyPackProductNumericId,
			practiceTestProductNumericId:
				currentExamContext.practiceTestProductNumericId,
			tenantId: tenantId,
			tenantReadableName: tenantReadableName,
		}),
		[
			currentExamContext,
			tenantId,
			tenantReadableName,
			userLogicalId,
			username,
			email,
		],
	);

	useEffect(() => {
		if (runOnMount && eventName && properties) {
			trackEvent(eventName, properties);
		}
	}, [eventName, properties, runOnMount]);

	const trackEvent = (
		eventName: RedeemCopilotEvent,
		properties?: Properties,
	) => {
		if (!analyticsEnabled) {
			return;
		}

		trackCustomEvent({
			eventName,
			...prepareProperties(baseProperties, properties),
		});
	};

	return { trackRedeemCopilotEvent: trackEvent };
};
